// src/components/ui/Autocomplete.tsx
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiX, FiPlus } from 'react-icons/fi';
import useOutsideClick from '../../hooks/useOutsideClick';

interface AutocompleteOption {
	value: string;
	label: string;
	description?: string;
}

interface AutocompleteProps {
	label: string;
	value: string;
	onChange: (value: string) => void;
	options: AutocompleteOption[];
	placeholder?: string;
	allowCustom?: boolean;
	required?: boolean;
	error?: string;
	disabled?: boolean;
	className?: string;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
	label,
	value,
	onChange,
	options,
	placeholder = 'Select or type...',
	allowCustom = true,
	required = false,
	error,
	disabled = false,
	className = ''
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [showCustomForm, setShowCustomForm] = useState(false);
	const [customValue, setCustomValue] = useState('');
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	useOutsideClick(dropdownRef, () => {
		setIsOpen(false);
		setShowCustomForm(false);
	});

	// Get display label for selected value
	const getDisplayLabel = (val: string) => {
		const option = options.find(opt => opt.value === val);
		return option ? option.label : val;
	};

	// Filter options based on input
	const filteredOptions = options.filter(option =>
		option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
		option.value.toLowerCase().includes(inputValue.toLowerCase())
	);

	// Handle option selection
	const handleOptionSelect = (optionValue: string) => {
		onChange(optionValue);
		setInputValue('');
		setIsOpen(false);
		setShowCustomForm(false);
	};

	// Handle custom value creation
	const handleCustomSubmit = () => {
		if (customValue.trim()) {
			onChange(customValue.trim());
			setCustomValue('');
			setShowCustomForm(false);
			setIsOpen(false);
			setInputValue('');
		}
	};

	// Handle input changes
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		setIsOpen(true);
		setShowCustomForm(false);

		// If user types exactly a custom value and allowCustom is true
		if (allowCustom && newValue && !options.some(opt =>
			opt.value.toLowerCase() === newValue.toLowerCase() ||
			opt.label.toLowerCase() === newValue.toLowerCase()
		)) {
			// Optionally auto-select custom values
		}
	};

	// Handle key press
	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && allowCustom && inputValue && filteredOptions.length === 0) {
			onChange(inputValue);
			setInputValue('');
			setIsOpen(false);
		}
	};

	// Clear selection
	const handleClear = () => {
		onChange('');
		setInputValue('');
		inputRef.current?.focus();
	};

	useEffect(() => {
		if (!isOpen) {
			setInputValue('');
		}
	}, [isOpen]);

	return (
		<div className={`relative ${className}`}>
			<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
				{label}
				{required && <span className="text-red-500 ms-1">*</span>}
			</label>

			<div ref={dropdownRef} className="relative">
				{/* Main Input */}
				<div className="relative">
					<input
						ref={inputRef}
						type="text"
						value={isOpen ? inputValue : getDisplayLabel(value)}
						onChange={handleInputChange}
						onKeyPress={handleKeyPress}
						onFocus={() => setIsOpen(true)}
						placeholder={value ? getDisplayLabel(value) : placeholder}
						disabled={disabled}
						className={`w-full px-4 py-3 pe-20 bg-gray-50 dark:bg-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:opacity-60 disabled:cursor-not-allowed text-gray-900 dark:text-white ${error
							? 'border-red-500 focus:ring-red-500'
							: 'border-gray-300 dark:border-gray-600'
							}`}
					/>

					{/* Actions */}
					<div className="absolute inset-y-0 end-0 flex items-center pe-3 gap-1">
						{value && !disabled && (
							<button
								type="button"
								onClick={handleClear}
								className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
							>
								<FiX className="h-4 w-4" />
							</button>
						)}
						<button
							type="button"
							onClick={() => setIsOpen(!isOpen)}
							disabled={disabled}
							className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
						>
							<FiChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
						</button>
					</div>
				</div>

				{/* Dropdown */}
				<AnimatePresence>
					{isOpen && (
						<motion.div
							initial={{ opacity: 0, y: -10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -10 }}
							transition={{ duration: 0.2 }}
							className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
						>
							{/* Options List */}
							{filteredOptions.length > 0 && (
								<div className="py-1">
									{filteredOptions.map((option) => (
										<button
											key={option.value}
											type="button"
											onClick={() => handleOptionSelect(option.value)}
											className="w-full px-4 py-3 text-start hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
										>
											<div className="font-medium text-gray-900 dark:text-white">
												{option.label}
											</div>
											{option.description && (
												<div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
													{option.description}
												</div>
											)}
										</button>
									))}
								</div>
							)}

							{/* Custom Value Form */}
							{allowCustom && (
								<>
									{filteredOptions.length > 0 && (
										<div className="border-t border-gray-200 dark:border-gray-700" />
									)}

									{!showCustomForm && !inputValue ? (
										<button
											type="button"
											onClick={() => setShowCustomForm(true)}
											className="w-full px-4 py-3 text-start text-primary hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
										>
											<FiPlus className="h-4 w-4" />
											<span className="font-medium">Add custom value</span>
										</button>
									) : showCustomForm ? (
										<div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
											<div className="flex gap-2">
												<input
													type="text"
													value={customValue}
													onChange={(e) => setCustomValue(e.target.value)}
													onKeyPress={(e) => e.key === 'Enter' && handleCustomSubmit()}
													placeholder="Enter custom value..."
													className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
													autoFocus
												/>
												<button
													type="button"
													onClick={handleCustomSubmit}
													disabled={!customValue.trim()}
													className="px-3 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
												>
													Add
												</button>
											</div>
										</div>
									) : null}
								</>
							)}

							{/* No options message */}
							{filteredOptions.length === 0 && !allowCustom && (
								<div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
									No options found
								</div>
							)}

							{/* Quick custom add for typed values */}
							{allowCustom && inputValue && filteredOptions.length === 0 && !showCustomForm && (
								<button
									type="button"
									onClick={() => onChange(inputValue)}
									className="w-full px-4 py-3 text-start text-primary hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
								>
									<FiPlus className="h-4 w-4" />
									<span>Add "{inputValue}"</span>
								</button>
							)}
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			{/* Error Message */}
			{error && (
				<motion.p
					initial={{ opacity: 0, y: -10 }}
					animate={{ opacity: 1, y: 0 }}
					className="mt-1 text-sm text-red-600 dark:text-red-500"
				>
					{error}
				</motion.p>
			)}
		</div>
	);
};

export default Autocomplete;